import HeadData from 'components/Common/HeadData'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import { useGlobalAirportTransferSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalAirportTransferSearch'
import { useGlobalCarHireSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalCarHireSearch'
import useIsCarHireSearchEnabled from 'hooks/GlobalSearch/useIsCarHireSearchEnabled'
import { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import CarsAirportTransferFAQs from './AirportTransfer/CarsAirportTransferFAQs'
import CarsAirportTransferHighlights from './AirportTransfer/CarsAirportTransferHighlights'
import CarsPageHeader, { type CarsTab } from './CarsPageHeader'
import CarsValuePropositions from './CarsValuePropositions'
import useAirportTransferValuePropositions from './hooks/useAirportTransferValuePropositions'
import useCarHireValuePropositions from './hooks/useCarHireValuePropositions'
import useAirportTransferSearchPrompt from './hooks/useAirportTransferSearchPrompt'
import useCarsTransferSearchURLState from './hooks/useCarsTransferSearchURLState'
import CarsCarHireSuppliers from './CarHire/CarsCarHireSuppliers'
import CarsCarHireFAQs from './CarHire/CarsCarHireFAQs'

function CarsPage() {
  const intl = useIntl()
  const { initialState, transferQueryParams } = useCarsTransferSearchURLState()

  const { globalAirportTransferSearchDispatch, globalAirportTransferSearchState } = useGlobalAirportTransferSearchContext(initialState, transferQueryParams)
  const { globalCarHireSearchDispatch, globalCarHireSearchState } = useGlobalCarHireSearchContext()
  const isCarHireSearchEnabled = useIsCarHireSearchEnabled()
  const { searchPromptRef, onAirportTransferSearchClick } = useAirportTransferSearchPrompt(globalAirportTransferSearchState)

  const [activeTab, setActiveTab] = useState<CarsTab>('airport-transfer')

  const isCarHireTab = activeTab === 'car-hire'
  const isAirportTransferTab = activeTab === 'airport-transfer'

  const airportTransferValuePropositions = useAirportTransferValuePropositions()
  const carHireValuePropositions = useCarHireValuePropositions()

  const onTabChange = useCallback((tab: CarsTab) => {
    setActiveTab(tab)
  }, [])

  let valuePropositions = airportTransferValuePropositions

  if (isCarHireTab && isCarHireSearchEnabled) {
    valuePropositions = carHireValuePropositions
  }

  return <>
    <HeadData
      title={
        intl.formatMessage({
          id: 'reRf7WXQCr_0oMboiU-c',
          defaultMessage: 'Transfers & Cars',
          description: 'Main heading for the placeholder Transfers & Cars landing page while its full experience is being built',
        })
      }
    />
    {isCarHireTab && isCarHireSearchEnabled && (
      <GlobalSearchContextProvider
        state={globalCarHireSearchState}
        dispatch={globalCarHireSearchDispatch}
      >
        <CarsPageHeader
          activeTab={activeTab}
          isCarHireSearchEnabled={isCarHireSearchEnabled}
          onTabChange={onTabChange}
        />
      </GlobalSearchContextProvider>
    )}
    {isAirportTransferTab && (
      <GlobalSearchContextProvider
        state={globalAirportTransferSearchState}
        dispatch={globalAirportTransferSearchDispatch}
      >
        <CarsPageHeader
          ref={searchPromptRef}
          activeTab={activeTab}
          onTabChange={onTabChange}
          isCarHireSearchEnabled={isCarHireSearchEnabled}
        />
      </GlobalSearchContextProvider>
    )}
    <CarsValuePropositions values={valuePropositions} />

    {isCarHireTab && isCarHireSearchEnabled && <CarsCarHireSuppliers />}
    {isAirportTransferTab && (
      <CarsAirportTransferHighlights onSearch={onAirportTransferSearchClick} />
    )}

    {isCarHireTab && isCarHireSearchEnabled && <CarsCarHireFAQs />}
    {isAirportTransferTab && <CarsAirportTransferFAQs />}
  </>
}

export default CarsPage
